import { format, isSameDay, isToday, isYesterday } from "date-fns";
import { ptBR } from "date-fns/locale";

import {
  SUPPORT_STATUS_LABEL,
  SupportRequest,
  SupportRequestAttachment,
  SupportRequestHistory,
  SupportRequestHistoryAction,
} from "../entities/SupportRequest";

export type ChatItem =
  | { kind: "date"; key: string; label: string }
  | {
      kind: "message";
      key: string;
      date: string;
      isOwn: boolean;
      authorLabel: string;
      showAuthor: boolean;
      text: string;
      attachments: SupportRequestAttachment[];
    }
  | { kind: "event"; key: string; date: string; text: string };

interface BuildChatItemsParams {
  supportRequest: SupportRequest;
  currentUserId?: string;
  isSupportStaff: boolean;
}

export const formatChatDay = (date: string | Date): string => {
  const parsed = new Date(date);
  if (isToday(parsed)) return "Hoje";
  if (isYesterday(parsed)) return "Ontem";
  return format(parsed, "dd/MM/yyyy", { locale: ptBR });
};

const statusLabel = (status?: string) =>
  (status &&
    SUPPORT_STATUS_LABEL[status as keyof typeof SUPPORT_STATUS_LABEL]) ||
  status ||
  "";

// Transforma a abertura do chamado + history em itens do chat, em ordem
// cronológica: separadores de dia, balões (agrupados por autor) e eventos.
export const buildChatItems = ({
  supportRequest,
  currentUserId,
  isSupportStaff,
}: BuildChatItemsParams): ChatItem[] => {
  type Raw =
    | {
        kind: "message";
        key: string;
        date: string;
        authorKey: string;
        isOwn: boolean;
        authorLabel: string;
        text: string;
        attachments: SupportRequestAttachment[];
      }
    | { kind: "event"; key: string; date: string; text: string };

  const raw: Raw[] = [];

  const openingText = supportRequest.description?.trim() ?? "";
  const openingAttachments = supportRequest.attachments ?? [];

  if (openingText || openingAttachments.length) {
    const isOwn = !isSupportStaff;
    raw.push({
      kind: "message",
      key: "opening",
      date: supportRequest.createdAt,
      authorKey: `seller:${supportRequest.seller?.id ?? "seller"}`,
      isOwn,
      authorLabel: isOwn ? "Você" : supportRequest.seller?.name || "Vendedor",
      text: openingText,
      attachments: openingAttachments,
    });
  }

  const history: SupportRequestHistory[] = [
    ...(supportRequest.history ?? []),
  ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  history.forEach((entry, index) => {
    const isOwn = !!currentUserId && entry.actor?.id === currentUserId;
    const actorName = isSupportStaff ? entry.actor?.name : undefined;
    const text = entry.message?.trim() ?? "";
    const attachments = entry.attachments ?? [];
    const key = `${entry.date}-${entry.action}-${index}`;

    if (entry.action === SupportRequestHistoryAction.ASSIGN_TICKET) {
      raw.push({
        kind: "event",
        key: `${key}-event`,
        date: entry.date,
        text: text || "Chamado reatribuído",
      });
      return;
    }

    if (text || attachments.length) {
      raw.push({
        kind: "message",
        key,
        date: entry.date,
        authorKey: `actor:${entry.actor?.id ?? entry.actor?.name ?? "support"}`,
        isOwn,
        authorLabel: isOwn ? "Você" : actorName || "Suporte",
        text,
        attachments,
      });
    }

    if (
      entry.action === SupportRequestHistoryAction.UPDATE_STATUS &&
      entry.to
    ) {
      const from = entry.from ? `${statusLabel(entry.from)} → ` : "";
      raw.push({
        kind: "event",
        key: `${key}-event`,
        date: entry.date,
        text: `Status alterado: ${from}${statusLabel(entry.to)}`,
      });
    }

    if (entry.action === SupportRequestHistoryAction.CLAIM_TICKET) {
      raw.push({
        kind: "event",
        key: `${key}-event`,
        date: entry.date,
        text: actorName
          ? `Chamado assumido por ${actorName}`
          : "Chamado assumido pelo suporte",
      });
    }
  });

  const items: ChatItem[] = [];
  let previous: Raw | undefined;

  raw.forEach((current) => {
    const newDay =
      !previous || !isSameDay(new Date(previous.date), new Date(current.date));

    if (newDay) {
      items.push({
        kind: "date",
        key: `day-${format(new Date(current.date), "yyyy-MM-dd")}`,
        label: formatChatDay(current.date),
      });
    }

    if (current.kind === "message") {
      const sameAuthor =
        !newDay &&
        previous?.kind === "message" &&
        previous.authorKey === current.authorKey &&
        previous.isOwn === current.isOwn;

      items.push({
        kind: "message",
        key: current.key,
        date: current.date,
        isOwn: current.isOwn,
        authorLabel: current.authorLabel,
        showAuthor: !sameAuthor,
        text: current.text,
        attachments: current.attachments,
      });
    } else {
      items.push(current);
    }

    previous = current;
  });

  return items;
};
